import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  ArrowLeft,
  Banknote,
  Coins,
  CheckCircle,
  Unlock,
  AlertTriangle,
} from 'lucide-react';

const DENOMINATIONS = [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];

const buildDenoms = () => DENOMINATIONS.map(value => ({ value, quantity: 0 }));

const getDenominationStyle = (value) => {
  if (value >= 20) return {
    gradient: 'from-emerald-500/25 to-cyan-500/15',
    border: 'border-emerald-400/30',
    labelKey: 'openCaisse.denomBill',
    icon: Banknote,
  };
  if (value >= 5) return {
    gradient: 'from-blue-500/25 to-indigo-500/15',
    border: 'border-blue-400/30',
    labelKey: 'openCaisse.denomBill',
    icon: Banknote,
  };
  return {
    gradient: 'from-amber-500/25 to-orange-500/15',
    border: 'border-amber-400/30',
    labelKey: 'openCaisse.denomCoin',
    icon: Coins,
  };
};

const DenominationCard = ({ item, onChange, testIdPrefix }) => {
  const { t, formatCurrency } = useLanguage();
  const style = getDenominationStyle(item.value);
  const Icon = style.icon;
  return (
    <div className={`rounded-2xl border ${style.border} bg-gradient-to-br ${style.gradient} p-4 shadow-lg shadow-black/10`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-black text-lg">{formatCurrency(item.value)}</div>
            <div className="text-xs uppercase tracking-wide text-gray-300">{t(style.labelKey)}</div>
          </div>
        </div>
        <div className="text-[11px] text-gray-200 px-2 py-1 rounded-full border border-white/10 bg-white/5">
          x {item.quantity}
        </div>
      </div>
      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="text-xs text-gray-300">
          {t('openCaisse.cardTotal')}
          <div className="text-white font-bold text-sm">{formatCurrency(item.value * item.quantity)}</div>
        </div>
        <input
          type="number"
          min="0"
          value={item.quantity}
          onChange={(e) => onChange(e.target.value)}
          className="input-dark w-24 py-2 text-center text-white font-semibold"
          data-testid={`${testIdPrefix}-${item.value}`}
        />
      </div>
    </div>
  );
};

const OpenCaissePage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const magasinId = (params.get('magasin_id') || user?.default_magasin_id || 'M1').toUpperCase();
  const forceMode = params.get('force') === '1';
  const [openDenoms, setOpenDenoms] = useState(buildDenoms());
  const [opening, setOpening] = useState(false);

  const totalOpen = useMemo(() => openDenoms.reduce((sum, item) => sum + item.value * item.quantity, 0), [openDenoms]);

  const updateDenoms = (index, value) => {
    const qty = Math.max(0, parseInt(value, 10) || 0);
    setOpenDenoms((prev) => prev.map((item, i) => (i === index ? { ...item, quantity: qty } : item)));
  };

  const handleOpenCaisse = async () => {
    setOpening(true);
    try {
      const endpoint = forceMode ? '/caisse/force-open' : '/caisse/ouvrir';
      await api.post(endpoint, {
        total: totalOpen,
        details: openDenoms,
        magasin_id: magasinId,
      });
      alert(t('openCaisse.successOpen', { magasin: magasinId }));
      navigate('/dashboard');
    } catch (error) {
      alert(error?.response?.data?.detail || t('openCaisse.errorOpen'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in space-y-6">
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-to-dashboard-open-caisse"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('openCaisse.backToDashboard')}
          </button>
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
            <Unlock className="w-6 h-6 inline-block mr-2" />
            {forceMode ? t('openCaisse.titleForce', { magasin: magasinId }) : t('openCaisse.title', { magasin: magasinId })}
          </h2>
          <div className="w-32" />
        </div>

        <div className="glass p-6 rounded-2xl">
          <div className="flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
            <div>
              <h3 className="text-white font-bold text-lg">{t('openCaisse.openingFund')}</h3>
              <p className="text-gray-400 text-sm">{t('openCaisse.openingFundHint', { magasin: magasinId })}</p>
            </div>
            <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 px-5 py-4 min-w-[220px]">
              <div className="text-sm text-emerald-200">{t('openCaisse.totalOpening')}</div>
              <div className="text-3xl font-black text-white">{formatCurrency(totalOpen)}</div>
            </div>
          </div>
          {forceMode && (
            <div className="mt-4 p-4 rounded-2xl bg-orange-500/10 border border-orange-500/30 text-orange-200 text-sm flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5" />
              <div>{t('openCaisse.forceWarning')}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {openDenoms.map((item, index) => (
            <DenominationCard
              key={item.value}
              item={item}
              onChange={(value) => updateDenoms(index, value)}
              testIdPrefix="open-denom"
            />
          ))}
        </div>

        <div className="glass p-6 rounded-2xl flex flex-col sm:flex-row gap-4 justify-end">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
          >
            {t('openCaisse.cancel')}
          </button>
          <button
            onClick={handleOpenCaisse}
            disabled={opening}
            className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            data-testid="validate-open-caisse-btn"
          >
            {opening ? t('openCaisse.opening') : <><CheckCircle className="w-5 h-5" /> {t('openCaisse.validateOpening')}</>}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default OpenCaissePage;
